<template>
  <div class="logs-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-left">
        <div class="page-title">
          <h1>
            <i class="el-icon-s-order"></i>
            日志管理
          </h1>
          <p class="page-subtitle">查看和管理机器人操作日志记录</p>
        </div>
      </div>
      <div class="header-right">
        <el-button 
          type="primary" 
          icon="el-icon-refresh" 
          size="medium"
          @click="refreshLogs"
          class="refresh-btn"
        >
          刷新
        </el-button>
      </div>
    </div>

    <!-- 筛选条件 -->
    <div class="filter-section">
      <el-card class="filter-card" shadow="never">
        <div class="filter-form">
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-item">
                <label>用户名</label>
                <el-input
                  v-model="filterForm.username"
                  placeholder="请输入用户名"
                  clearable
                  @clear="handleFilter"
                  @keyup.enter.native="handleFilter"
                />
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-item">
                <label>机器人名称</label>
                <el-input
                  v-model="filterForm.robot_name"
                  placeholder="请输入机器人名称"
                  clearable
                  @clear="handleFilter"
                  @keyup.enter.native="handleFilter"
                />
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-item">
                <label>设备类型</label>
                <el-select
                  v-model="filterForm.device_type"
                  placeholder="请选择设备类型"
                  clearable
                  @change="handleFilter"
                >
                  <el-option label="全部类型" value="" />
                  <el-option label="单开型" value="single" />
                  <el-option label="双开型" value="double" />
                  <el-option label="兼容型" value="compatible" />
                </el-select>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-item">
                <label>操作类型</label>
                <el-select
                  v-model="filterForm.action"
                  placeholder="请选择操作类型"
                  clearable
                  @change="handleFilter"
                >
                  <el-option label="全部操作类型" value="" />
                  <el-option label="开启" value="single_on" />
                  <el-option label="左开" value="double_left" />
                  <el-option label="右开" value="double_right" />
                  <el-option label="切换" value="single_switch" />
                  <el-option label="左右切换" value="double_switch" />
                  <el-option label="关闭" value="off" />
                </el-select>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-item">
                <label>执行结果</label>
                <el-select
                  v-model="filterForm.result"
                  placeholder="请选择执行结果"
                  clearable
                  @change="handleFilter"
                >
                  <el-option label="全部" value="" />
                  <el-option label="成功" value="success" />
                  <el-option label="失败" value="failed" />
                  <el-option label="超时" value="timeout" />
                </el-select>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-item">
                <label>开始时间</label>
                <el-date-picker
                  v-model="filterForm.start_time"
                  type="datetime"
                  placeholder="选择开始时间"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  @change="handleFilter"
                />
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-item">
                <label>结束时间</label>
                <el-date-picker
                  v-model="filterForm.end_time"
                  type="datetime"
                  placeholder="选择结束时间"
                  format="yyyy-MM-dd HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  @change="handleFilter"
                />
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6" :xl="4">
              <div class="filter-actions">
                <el-button type="primary" @click="handleFilter" icon="el-icon-search">
                  搜索
                </el-button>
                <el-button @click="resetFilter" icon="el-icon-refresh-left">
                  重置
                </el-button>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 日志列表 -->
    <div class="logs-content">
      <el-card class="logs-card" shadow="never">
        <div class="logs-header">
          <div class="logs-title">
            <h3>操作日志</h3>
            <span class="logs-count">共 {{ logData.total || 0 }} 条记录</span>
          </div>
        </div>

        <div class="logs-table">
          <el-table
            :data="logData.list || []"
            v-loading="loading"
            stripe
            :default-sort="{ prop: 'created_at', order: 'descending' }"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
            class="logs-table-content"
          >
            <el-table-column prop="id" label="ID" align="center" width="80" />
            <el-table-column prop="username" label="用户名" align="center" min-width="120">
              <template slot-scope="scope">
                <el-tag size="small" type="info">{{ scope.row.username }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="robot_name" label="机器人名称" align="center" min-width="150">
              <template slot-scope="scope">
                <el-tag size="small" type="primary">{{ scope.row.robot_name }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="device_type" label="设备类型" width="110">
              <template slot-scope="scope">
                <el-tag size="small" :type="getDeviceTypeTagType(scope.row.device_type)">
                  {{ getDeviceTypeText(scope.row.device_type) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="mqtt_topic" label="MQTT主题" align="center" min-width="120" show-overflow-tooltip />
            <el-table-column prop="action" label="操作类型" align="center" width="100">
              <template slot-scope="scope">
                <el-tag size="small" :type="getActionTagType(scope.row.action)">
                  {{ getActionText(scope.row.action) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="result" label="执行结果" align="center" width="100">
              <template slot-scope="scope">
                <el-tag size="small" :type="getResultTagType(scope.row.result)">
                  {{ getResultText(scope.row.result) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="client_ip" label="客户端IP" align="center" min-width="120" />
            <el-table-column prop="created_at" label="执行时间" align="center" min-width="180" sortable>
              <template slot-scope="scope">
                <span>{{ formatDateTime(scope.row.created_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" align="center" width="100">
              <template slot-scope="scope">
                <el-button
                  type="text"
                  size="small"
                  @click="showLogDetail(scope.row)"
                  icon="el-icon-view"
                  class="action-btn view-btn"
                >
                  详情
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 分页 -->
        <div class="pagination-container">
          <el-pagination
            @size-change="handlePageSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[10, 20, 30, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="logData.total || 0"
            background
          />
        </div>
      </el-card>
    </div>

    <!-- 日志详情对话框 -->
    <el-dialog
      title="日志详情"
      :visible.sync="detailDialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <div v-if="selectedLog" class="log-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="日志ID">{{ selectedLog.id }}</el-descriptions-item>
          <el-descriptions-item label="用户ID">{{ selectedLog.user_id }}</el-descriptions-item>
          <el-descriptions-item label="用户名">
            <el-tag size="small" type="info">{{ selectedLog.username }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="机器人ID">{{ selectedLog.robot_id }}</el-descriptions-item>
          <el-descriptions-item label="机器人名称">
            <el-tag size="small" type="primary">{{ selectedLog.robot_name }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="设备类型">
            <el-tag size="small" :type="getDeviceTypeTagType(selectedLog.device_type)">
              {{ getDeviceTypeText(selectedLog.device_type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="MQTT主题" :span="2">{{ selectedLog.mqtt_topic }}</el-descriptions-item>
          <el-descriptions-item label="操作类型">
            <el-tag size="small" :type="getActionTagType(selectedLog.action)">
              {{ getActionText(selectedLog.action) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="执行结果">
            <el-tag size="small" :type="getResultTagType(selectedLog.result)">
              {{ getResultText(selectedLog.result) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="客户端IP">{{ selectedLog.client_ip }}</el-descriptions-item>
          <el-descriptions-item label="执行时间">{{ formatDateTime(selectedLog.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="UserAgent" :span="2">
            <span class="user-agent">{{ selectedLog.user_agent || '无' }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="定时任务执行">{{ selectedLog.is_scheduled_task ? "是" : "否" }}</el-descriptions-item>
          <el-descriptions-item label="备注">{{ selectedLog.remark }}</el-descriptions-item>
          <el-descriptions-item v-if="selectedLog.error_message" label="错误信息" :span="2">
            <el-alert
              :title="selectedLog.error_message"
              type="error"
              :closable="false"
              show-icon
            />
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getRobotLogs } from '@/api/log'

export default {
  name: 'AdminLogs',
  data() {
    return {
      loading: false,
      logData: {
        list: [],
        total: 0,
        page: 1,
        page_size: 10,
        pages: 0
      },
      pagination: {
        page: 1,
        pageSize: 10
      },
      filterForm: {
        username: '',
        robot_name: '',
        device_type: '',
        action: '',
        result: '',
        start_time: '',
        end_time: ''
      },
      detailDialogVisible: false,
      selectedLog: null
    }
  },
  mounted() {
    this.loadLogs()
  },
  methods: {
    // 加载日志数据
    async loadLogs() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
          ...this.filterForm
        }
        
        // 清理空值参数
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const response = await getRobotLogs(params)
        
        if (response && response.data) {
          this.logData = response.data
          this.pagination.page = response.data.page
          this.pagination.pageSize = response.data.page_size
        } else {
          console.warn('API响应数据格式异常:', response)
          // 如果数据结构不符合预期，初始化默认值
          this.logData = {
            list: [],
            total: 0,
            page: 1,
            page_size: 10,
            pages: 0
          }
        }
      } catch (error) {
        console.error('加载日志失败:', error)
      } finally {
        this.loading = false
      }
    },

    // 刷新日志
    refreshLogs() {
      this.pagination.page = 1
      this.loadLogs()
    },

    // 筛选处理
    handleFilter() {
      this.pagination.page = 1
      this.loadLogs()
    },

    // 重置筛选条件
    resetFilter() {
      this.filterForm = {
        username: '',
        robot_name: '',
        device_type: '',
        action: '',
        result: '',
        start_time: '',
        end_time: ''
      }
      this.pagination.page = 1
      this.loadLogs()
    },

    // 分页大小改变
    handlePageSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.loadLogs()
    },

    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadLogs()
    },

    // 显示日志详情
    showLogDetail(log) {
      this.selectedLog = log
      this.detailDialogVisible = true
    },

    // 获取设备类型标签类型
    getDeviceTypeTagType(type) {
      const typeMap = {
        'single': 'success',
        'double': 'primary',
        'compatible': 'warning'
      }
      return typeMap[type] || 'info'
    },

    // 获取设备类型文本
    getDeviceTypeText(type) {
      const typeMap = {
        'single': '单开型',
        'double': '双开型',
        'compatible': '兼容型',
      }
      return typeMap[type] || type
    },

    // 获取操作类型标签类型
    getActionTagType(action) {
      const actionMap = {
        'single_on': 'success',
        'double_left': 'warning',
        'double_right': 'warning',
        'single_switch': 'primary',
        'double_switch': 'primary',
        'off': 'danger',
      }
      return actionMap[action] || 'info'
    },

    // 获取操作类型文本
    getActionText(action) {
      const actionMap = {
        'single_on': '开启',
        'double_left': '左开',
        'double_right': '右开',
        'single_switch': '切换',
        'double_switch': '左右切换',
        'off': '关闭',
      }
      return actionMap[action] || action
    },

    // 获取执行结果标签类型
    getResultTagType(result) {
      const resultMap = {
        'success': 'success',
        'failed': 'danger',
        'timeout': 'warning'
      }
      return resultMap[result] || 'info'
    },

    // 获取执行结果文本
    getResultText(result) {
      const resultMap = {
        'success': '成功',
        'failed': '失败',
        'timeout': '超时'
      }
      return resultMap[result] || result
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return ''
      const date = new Date(dateTime)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.logs-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.page-title h1 {
  margin: 0 0 8px 0;
  color: #ffffff;
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title h1 i {
  font-size: 32px;
}

.page-subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
}

.refresh-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.filter-section {
  margin-bottom: 20px;
}

.filter-card {
  border-radius: 12px;
  border: none;
}

.filter-form {
  padding: 10px 0;
}

.filter-item {
  margin-bottom: 15px;
}

.filter-item label {
  display: block;
  margin-bottom: 5px;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
}

.filter-actions {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  height: 100%;
  padding-top: 25px;
}

.logs-content {
  margin-bottom: 20px;
}

.logs-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
}

.logs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #ebeef5;
}

.logs-title h3 {
  margin: 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.logs-count {
  margin-left: 10px;
  color: #909399;
  font-size: 14px;
}

.logs-table {
  margin-bottom: 20px;
}

.logs-table-content {
  width: 100%;
}

.action-btn {
  padding: 4px 4px;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s;
}

.view-btn {
  color: #409eff;
}

.view-btn:hover {
  background-color: #ecf5ff;
  color: #337ecc;
}

.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
}

.pagination-container .el-pagination {
  background: white;
}

.log-detail {
  padding: 10px 0;
}

.user-agent {
  word-break: break-all;
  font-size: 12px;
  color: #909399;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .logs-page {
    padding: 10px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .filter-actions {
    padding-top: 0;
    justify-content: center;
  }
  
  .logs-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .page-size-selector {
    align-self: stretch;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .filter-form .el-row {
    margin: 0;
  }
  
  .filter-form .el-col {
    padding: 0;
    margin-bottom: 15px;
  }
  
  .filter-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-actions .el-button {
    width: 100%;
  }
}
</style>
